export const ACTION_IDS = ["TracePrecedents", "TraceDependents", "OpenGridrive"];
export const ACTION_LABELS = {
    TracePrecedents: "Trace Precedents",
    TraceDependents: "Trace Dependents",
    OpenGridrive: "Open Gridrive",
};
const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
export const DEFAULT_SHORTCUTS = {
    TracePrecedents: isMac ? "Command+Shift+U" : "Ctrl+Shift+J",
    TraceDependents: isMac ? "Command+Shift+I" : "Ctrl+Shift+K",
    OpenGridrive: isMac ? "Command+Shift+D" : "Ctrl+Shift+D",
};
const TEMP_SHORTCUTS = {
    TracePrecedents: isMac ? "Command+Shift+F6" : "Ctrl+Shift+F6",
    TraceDependents: isMac ? "Command+Shift+F7" : "Ctrl+Shift+F7",
    OpenGridrive: isMac ? "Command+Shift+F8" : "Ctrl+Shift+F8",
};
const WEB_BLOCKED_COMBOS = new Set([
    "Ctrl+N",
    "Ctrl+Shift+N",
    "Ctrl+T",
    "Ctrl+Shift+T",
    "Ctrl+W",
    "Ctrl+PageUp",
    "Ctrl+PageDown",
]);
function isOfficeWeb() {
    try {
        return Office?.context?.platform === Office.PlatformType.OfficeOnline;
    }
    catch {
        return false;
    }
}
export function formatComboForDisplay(combo) {
    const parts = combo.split("+").map((p) => p.trim());
    const mapped = parts.map((part) => {
        if (isMac) {
            if (part === "Command" || part === "Cmd" || part === "Ctrl" || part === "Control")
                return "⌘";
            if (part === "Shift")
                return "⇧";
            if (part === "Alt" || part === "Option")
                return "⌥";
        }
        else {
            if (part === "Command" || part === "Cmd")
                return "Ctrl";
            if (part === "Control")
                return "Ctrl";
            if (part === "Ctrl")
                return "Ctrl";
            if (part === "Shift")
                return "⇧";
            if (part === "Alt" || part === "Option")
                return "Alt";
        }
        return part;
    });
    return mapped.join(" ");
}
export function isValidCombo(combo) {
    if (!combo)
        return false;
    const parts = combo.split("+").map((p) => p.trim());
    if (parts.length < 2)
        return false;
    const modifiers = new Set();
    let key = "";
    for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p === "Ctrl" || p === "Control" || p === "Command" || p === "Cmd" || p === "Alt" || p === "Option" || p === "Shift") {
            modifiers.add(p);
        }
        else {
            if (i !== parts.length - 1)
                return false;
            key = p;
        }
    }
    if (!key)
        return false;
    if (!/^[A-Z0-9\-_+]$/.test(key))
        return false;
    const hasNonShiftModifier = modifiers.has("Ctrl") ||
        modifiers.has("Control") ||
        modifiers.has("Command") ||
        modifiers.has("Cmd") ||
        modifiers.has("Alt") ||
        modifiers.has("Option");
    if (!hasNonShiftModifier)
        return false;
    if (isOfficeWeb() && WEB_BLOCKED_COMBOS.has(combo))
        return false;
    return true;
}
export async function loadCurrentShortcuts() {
    const empty = {
        TracePrecedents: null,
        TraceDependents: null,
        OpenGridrive: null,
    };
    if (!Office.actions?.getShortcuts) {
        return { ...DEFAULT_SHORTCUTS };
    }
    try {
        const result = await Office.actions.getShortcuts();
        const out = { ...empty };
        for (const id of ACTION_IDS) {
            out[id] = id in result ? result[id] : DEFAULT_SHORTCUTS[id];
        }
        return out;
    }
    catch {
        return { ...DEFAULT_SHORTCUTS };
    }
}
export async function saveShortcut(id, combo) {
    if (!Office.actions?.replaceShortcuts)
        return { ok: false, reason: "unsupported" };
    if (!isValidCombo(combo))
        return { ok: false, reason: "invalid" };
    try {
        await Office.actions.replaceShortcuts({ [id]: combo });
        if (Office.actions.getShortcuts) {
            const after = await Office.actions.getShortcuts();
            if (after?.[id] !== combo)
                return { ok: false, reason: "notPersisted" };
        }
        return { ok: true };
    }
    catch {
        return { ok: false, reason: "error" };
    }
}
// The TEMP → DEFAULT → null zig-zag is a workaround for Excel caching the
// user's "use as native" choice. A single replaceShortcuts(DEFAULT) is
// silently ignored once that cache is set; reassigning to filler combos
// and then to null forces Excel to drop the cached decision and fall
// back to the manifest defaults. Do not simplify.
export async function resetShortcut(id) {
    const replace = Office.actions?.replaceShortcuts;
    if (!replace)
        return;
    try {
        await replace({ [id]: TEMP_SHORTCUTS[id] });
    }
    catch { /* step-optional */ }
    try {
        await replace({ [id]: DEFAULT_SHORTCUTS[id] });
    }
    catch { /* step-optional */ }
    await replace({ [id]: null });
}
export async function resetAllShortcuts() {
    const replace = Office.actions?.replaceShortcuts;
    if (!replace)
        return false;
    try {
        await replace({ ...TEMP_SHORTCUTS });
    }
    catch { /* step-optional */ }
    try {
        await replace({ ...DEFAULT_SHORTCUTS });
    }
    catch { /* step-optional */ }
    await replace({
        TracePrecedents: null,
        TraceDependents: null,
        OpenGridrive: null,
    });
    return true;
}
